import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Dropdown,
  Option,
  Input,
  Checkbox,
} from '@fluentui/react-components';
import {
  ChevronRightRegular,
  ChevronLeftRegular,
  ChevronDoubleRightRegular,
  ChevronDoubleLeftRegular,
  DismissRegular,
  ReOrderDotsVerticalRegular,
} from '@fluentui/react-icons';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Question, TenderingPart } from '../../models/tendering';
import { deepCloneObject } from '../../utils/clone';

interface QuestionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (data: { selected: any[]; returnData?: any }) => void;
  tenderingParts: TenderingPart[];
  singleSelect?: boolean;
  selectedQuestions?: { questionID: number }[];
  filterID?: string;
  returnData?: any;
  questionIDs?: number[];
  onlyType?: string;
}

interface SortableSelectedItemProps {
  question: Question;
  targetFilter: string;
  isChecked: boolean;
  onCheck: (questionID: number, checked: boolean) => void;
}

const SortableSelectedItem: React.FC<SortableSelectedItemProps> = ({
  question,
  targetFilter,
  isChecked,
  onCheck,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: question.questionID,
    disabled: !!targetFilter,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-start gap-2 p-2 hover:bg-gray-100 border-b border-gray-200"
    >
      {!targetFilter && (
        <div {...attributes} {...listeners} className="cursor-move pt-1" title="Dra för att ändra ordning">
          <ReOrderDotsVerticalRegular className="text-gray-500" />
        </div>
      )}
      <Checkbox
        checked={isChecked}
        onChange={(_, data) =>
          onCheck(question.questionID, data.checked as boolean)
        }
      />
      <div className="flex-1">
        <div className="font-semibold text-sm">{question.heading}</div>
        <div className="text-xs text-gray-500">{question.questionType}</div>
        <div className="text-sm">{question.text}</div>
      </div>
    </div>
  );
};

const QuestionDialog: React.FC<QuestionDialogProps> = ({
  isOpen,
  onClose,
  onSelect,
  tenderingParts,
  singleSelect = false,
  selectedQuestions,
  filterID,
  returnData,
  questionIDs,
  onlyType,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const [parts, setParts] = useState<TenderingPart[]>([]);
  const [selectedTenderingPart, setSelectedTenderingPart] = useState<TenderingPart | null>(null);
  const [source, setSource] = useState<Question[]>([]);
  const [selected, setSelected] = useState<any[]>([]);
  const [sourceFilter, setSourceFilter] = useState('');
  const [targetFilter, setTargetFilter] = useState('');
  const [sourceChecked, setSourceChecked] = useState<Set<number>>(new Set());
  const [targetChecked, setTargetChecked] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (isOpen) {
      const clonedParts = deepCloneObject(tenderingParts);
      setParts(clonedParts);
      setSelectedTenderingPart(clonedParts[0]);
      initializeSelected(clonedParts);
      
      if (questionIDs) {
        getBaseFilterQuestions(clonedParts);
      } else {
        selectTenderingPart(clonedParts[0].tenderingPartID, clonedParts);
      }
    }
  }, [isOpen]);

  const initializeSelected = (clonedParts: TenderingPart[]) => {
    const initialSelected: Question[] = [];
    if (selectedQuestions) {
      selectedQuestions.forEach((sq) => {
        clonedParts.forEach((tp: TenderingPart) => {
          const found = tp.questions?.filter((q) => q.questionID === sq.questionID) || [];
          initialSelected.push(...found);
        });
      });
    }
    setSelected(initialSelected);
  };

  const selectTenderingPart = (tendID: number, clonedParts?: TenderingPart[]) => {
    const partsToUse = clonedParts || parts;
    const part = partsToUse.find((tend) => tend.tenderingPartID === tendID);
    if (!part) return;

    setSelectedTenderingPart(part);
    let questions = [...(part.questions || [])];

    // Remove already selected questions
    if (selectedQuestions) {
      const selectedIds = selected.map((s) => s.questionID);
      questions = questions.filter((f) => !selectedIds.includes(f.questionID));
    }

    // Filter by question type if specified
    if (onlyType) {
      questions = questions.filter((f) => f.questionType === onlyType);
    }

    setSource(questions);
  };

  const getBaseFilterQuestions = (clonedParts: TenderingPart[]) => {
    let allQuestions: Question[] = [];
    clonedParts.forEach((tp) => {
      allQuestions = [...allQuestions, ...(tp.questions || [])];
    });

    const filtered = allQuestions.filter((q) => questionIDs!.includes(q.questionID));
    setSource(filtered);
  };

  const filterQuestions = (questions: Question[], searchText: string): Question[] => {
    if (!searchText) return questions;
    const lower = searchText.toLowerCase();
    return questions.filter(
      (q) =>
        q.text?.toLowerCase().includes(lower) || q.heading?.toLowerCase().includes(lower)
    );
  };

  const moveToTarget = () => {
    const toMove = source.filter((q) => sourceChecked.has(q.questionID));
    
    if (singleSelect && toMove.length > 0) {
      // In single select, only move the first item and replace selected
      setSelected([toMove[0]]);
      setSource(source.filter((q) => q.questionID !== toMove[0].questionID));
    } else {
      setSelected([...selected, ...toMove]);
      setSource(source.filter((q) => !sourceChecked.has(q.questionID)));
    }
    setSourceChecked(new Set());
  };

  const moveAllToTarget = () => {
    if (singleSelect && source.length > 0) {
      setSelected([source[0]]);
      setSource(source.slice(1));
    } else {
      setSelected([...selected, ...source]);
      setSource([]);
    }
    setSourceChecked(new Set());
  };

  const moveToSource = () => {
    const toMove = selected.filter((q) => targetChecked.has(q.questionID));
    setSource([...source, ...toMove]);
    setSelected(selected.filter((q) => !targetChecked.has(q.questionID)));
    setTargetChecked(new Set());
  };

  const moveAllToSource = () => {
    setSource([...source, ...selected]);
    setSelected([]);
    setTargetChecked(new Set());
  };

  const handleSourceCheck = (questionID: number, checked: boolean) => {
    const newChecked = new Set(sourceChecked);
    if (checked) {
      if (singleSelect) {
        newChecked.clear();
      }
      newChecked.add(questionID);
    } else {
      newChecked.delete(questionID);
    }
    setSourceChecked(newChecked);
  };

  const handleTargetCheck = (questionID: number, checked: boolean) => {
    const newChecked = new Set(targetChecked);
    if (checked) {
      if (singleSelect) {
        newChecked.clear();
      }
      newChecked.add(questionID);
    } else {
      newChecked.delete(questionID);
    }
    setTargetChecked(newChecked);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setSelected((items) => {
        const oldIndex = items.findIndex((q) => q.questionID === active.id);
        const newIndex = items.findIndex((q) => q.questionID === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleSave = () => {
    const result = [...selected];
    if (filterID && result.length > 0) {
      result[0].filterID = filterID;
    }
    onSelect({ selected: result, returnData });
  };

  const handleClose = () => {
    onClose();
  };

  const filteredSource = filterQuestions(source, sourceFilter);
  const filteredTarget = filterQuestions(selected, targetFilter);

  return (
    <Dialog modalType='alert' open={isOpen} onOpenChange={(_, data) => !data.open && handleClose()}>
      <DialogSurface style={{ maxWidth: '1200px', width: '90vw' }}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                aria-label="close"
                icon={<DismissRegular />}
                onClick={handleClose}
              />
            }
          >
            Koppla {singleSelect ? 'fråga' : 'frågor'}
          </DialogTitle>
          <DialogContent className="space-y-4">
            {/* Tendering Part Selector */}
            {!questionIDs && (
              <div>
                <h5 className="text-sm font-semibold mb-2">VÄLJ AVTALSOMRÅDE</h5>
                <Dropdown
                  placeholder="Välj Avtalsområde"
                  value={selectedTenderingPart?.tenderingPart || ''}
                  onOptionSelect={(_, data) =>
                    selectTenderingPart(Number(data.optionValue))
                  }
                  className="w-full"
                >
                  {parts.map((tp) => (
                    <Option key={tp.tenderingPartID} value={tp.tenderingPartID.toString()}>
                      {tp.tenderingPart}
                    </Option>
                  ))}
                </Dropdown>
              </div>
            )}

            {/* Pick List */}
            <div className="flex gap-4 items-start">
              {/* Source List */}
              <div className="flex-1 border border-gray-300 rounded">
                <div className="bg-gray-100 p-2 font-semibold border-b border-gray-300">
                  Frågor
                </div>
                <div className="p-2">
                  <Input
                    placeholder="Sök efter fråga"
                    value={sourceFilter}
                    onChange={(e) => setSourceFilter(e.target.value)}
                    className="w-full mb-2"
                  />
                </div>
                <div className="overflow-y-auto h-[500px] px-2 pb-2">
                  {filteredSource.map((question) => (
                    <div
                      key={question.questionID}
                      className="flex items-start gap-2 p-2 hover:bg-gray-100 border-b border-gray-200"
                    >
                      <Checkbox
                        checked={sourceChecked.has(question.questionID)}
                        onChange={(_, data) =>
                          handleSourceCheck(question.questionID, data.checked as boolean)
                        }
                      />
                      <div className="flex-1">
                        <div className="font-semibold text-sm">{question.heading}</div>
                        <div className="text-xs text-gray-500">{question.questionType}</div>
                        <div className="text-sm">{question.text}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Transfer Buttons */}
              <div className="flex flex-col gap-2 justify-center">
                <Button
                  appearance="subtle"
                  icon={<ChevronRightRegular />}
                  onClick={moveToTarget}
                  disabled={sourceChecked.size === 0}
                  title="Flytta valda"
                />
                <Button
                  appearance="subtle"
                  icon={<ChevronDoubleRightRegular />}
                  onClick={moveAllToTarget}
                  disabled={source.length === 0}
                  title="Flytta alla"
                />
                <Button
                  appearance="subtle"
                  icon={<ChevronLeftRegular />}
                  onClick={moveToSource}
                  disabled={targetChecked.size === 0}
                  title="Flytta tillbaka valda"
                />
                <Button
                  appearance="subtle"
                  icon={<ChevronDoubleLeftRegular />}
                  onClick={moveAllToSource}
                  disabled={selected.length === 0}
                  title="Flytta tillbaka alla"
                />
              </div>

              {/* Target List */}
              <div className="flex-1 border border-gray-300 rounded">
                <div className="bg-gray-100 p-2 font-semibold border-b border-gray-300">
                  Vald
                </div>
                <div className="p-2">
                  <Input
                    placeholder="Sök efter fråga"
                    value={targetFilter}
                    onChange={(e) => setTargetFilter(e.target.value)}
                    className="w-full mb-2"
                  />
                </div>
                <div className="overflow-y-auto h-[500px] px-2 pb-2">
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={selected.map((q) => q.questionID)}
                      strategy={verticalListSortingStrategy}
                    >
                      {filteredTarget.map((question) => (
                        <SortableSelectedItem
                          key={question.questionID}
                          question={question}
                          targetFilter={targetFilter}
                          isChecked={targetChecked.has(question.questionID)}
                          onCheck={handleTargetCheck}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                </div>
              </div>
            </div>
          </DialogContent>
          <DialogActions className="pt-6">
            <Button appearance="secondary" onClick={handleClose}>
              Avbryt
            </Button>
            <Button appearance="primary" onClick={handleSave}>
              OK
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default QuestionDialog;
